import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import makeStyles from '@mui/styles/makeStyles';
import { ErrorMessage } from 'formik';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import TrueLinkIcon from 'react/shared/components/true_link/main/TrueLinkIcon';
import TrueLinkTooltip from 'react/shared/components/true_link/main/TrueLinkTooltip';
import TrueLinkCheckboxes from 'react/shared/components/true_link/main/form/checkboxes/TrueLinkCheckboxes';
import TrueLinkRadios from 'react/shared/components/true_link/main/form/radios/TrueLinkRadios';
import TrueLinkTextInput from 'react/shared/components/true_link/main/form/text_input/TrueLinkTextInput';
import CardText from 'react/shared/components/true_link/main/form/wizard/CardText';
import { smallBreakpointMediaQuery } from 'react/shared/theme/Theme';
import PALETTE from 'react/shared/theme/palette';

const useStyles = makeStyles(() => ({
  cardForOtherTextbox: {
    margin: 0,
    marginBottom: '24px',
  },
  cardForPaidCaregiverRadios: {
    marginBottom: '24px',
  },
  errorMessage: {
    color: PALETTE.red,
    marginBottom: '12px',
  },
}));

export default function RolesForm({
  onChange,
  setFieldValue,
  setFieldTouched,
  values,
  paidCaregiverQuestionIsRequired,
  parentGuardianQuestionIsRequired,
}) {
  const classes = useStyles();
  const inline = useMediaQuery(smallBreakpointMediaQuery);
  const cardForCaregiverOptions = ['disability', 'elder', 'mental-health-challenges', 'other'];

  useEffect(() => {
    if (
      paidCaregiverQuestionIsRequired(values.cardFor) ||
      parentGuardianQuestionIsRequired(values.cardFor)
    ) {
      window.scrollTo({ left: 0, top: document.body.scrollHeight, behavior: 'smooth' });
    }
  }, [paidCaregiverQuestionIsRequired, parentGuardianQuestionIsRequired, values.cardFor]);

  const handleCheckboxCheck = (event) => {
    if (event.target.checked) {
      setFieldValue('cardFor', values.cardFor.concat(event.target.name));
    } else {
      //Reset values if the checkbox is unchecked
      if (event.target.name === 'teen-or-child') {
        setFieldValue('cardForTeenOrChild', ''); // Removes old value
      }
      if (cardForCaregiverOptions.find((option) => option === event.target.name)) {
        const multipleCaregiverOptions = values.cardFor.filter((name) =>
          cardForCaregiverOptions.find((option) => option === name),
        );
        if (multipleCaregiverOptions.length === 1) {
          setFieldValue('cardForPaidCaregiver', '');
        }
      }
      //Remove the checkbox value from the cardFor array
      setFieldValue(
        'cardFor',
        values.cardFor.filter((name) => name !== event.target.name),
      );
    }
  };

  const handleRadioSelect = (event, type = 'caregiver') => {
    if (type === 'caregiver') {
      setFieldValue('cardForPaidCaregiver', event.target.value);
    } else if (type === 'teen-or-child') {
      setFieldTouched('cardForTeenOrChild', true);
      setFieldValue('cardForTeenOrChild', event.target.value);
    }
  };

  const options = [
    {
      label: 'An older adult',
      value: 'elder',
    },
    {
      label: 'Someone in recovery',
      value: 'recovery',
    },
    {
      label: 'Someone with a disability',
      value: 'disability',
    },
    {
      label: 'Someone with mental health challenges',
      value: 'mental-health-challenges',
    },
    {
      label: 'Other',
      value: 'other',
    },
  ];

  if ($tlf.flipperShowChildSignUpFlow) {
    options.splice(4, 0, {
      label: 'Teen or child',
      value: 'teen-or-child',
    });
  }

  const paidCaregiverAnswerOptions = [
    {
      label: 'Yes',
      value: 'yes',
      id: 'paidCaregiverYesOption',
    },
    {
      label: 'Maybe',
      value: 'maybe',
      id: 'paidCaregiverMaybeOption',
    },
    {
      label: 'No',
      value: 'no',
      id: 'paidCaregiverNoOption',
    },
  ];

  const parentGuardianAnswerOptions = [
    {
      label: 'Yes',
      value: 'yes',
      id: 'parentGuardianYesOption',
    },

    {
      label: 'No',
      value: 'no',
      id: 'parentGuardianNoOption',
    },
  ];

  return (
    <>
      <CardText>
        Knowing their needs allows us to help you choose the right card program for them.
      </CardText>
      <Grid container spacing={inline ? 3 : 0}>
        <Grid item sm={6} xs={12}>
          <TrueLinkTextInput
            labelText="Cardholder first name"
            name="firstName"
            onChange={onChange}
            placeholder="First name"
            required
            setFieldTouched={setFieldTouched}
            value={values.firstName}
          />
        </Grid>
        <Grid item sm={6} xs={12}>
          <TrueLinkTextInput
            labelText="Cardholder last name"
            name="lastName"
            onChange={onChange}
            placeholder="Last name"
            required
            setFieldTouched={setFieldTouched}
            value={values.lastName}
          />
        </Grid>
      </Grid>
      <CardText>The cardholder is... (select all that apply) </CardText>
      <TrueLinkCheckboxes
        ariaLabel="cardholder description"
        name=""
        onChange={handleCheckboxCheck}
        options={options}
        required
        value={values.cardFor}
      />
      {values.cardFor.includes('other') && (
        <TrueLinkTextInput
          className={classes.cardForOtherTextbox}
          name="cardForOther"
          onChange={onChange}
          placeholder="Briefly describe their need"
          required
          setFieldTouched={setFieldTouched}
          value={values.cardForOther}
        />
      )}

      <ErrorMessage className={classes.errorMessage} component="p" name="cardFor" />

      {paidCaregiverQuestionIsRequired(values.cardFor) && (
        <>
          <CardText>
            Will a paid caregiver be using the card to make purchases?{' '}
            <TrueLinkTooltip
              tooltipContent={
                <Typography>
                  We collect this information so that we can set your card up correctly.
                </Typography>
              }
            >
              <TrueLinkIcon className={classes.activityIcon} icon="circle-question fa-regular" />
            </TrueLinkTooltip>
          </CardText>
          <ErrorMessage
            className={classes.errorMessage}
            component="p"
            name="cardForPaidCaregiver"
          />
          <div className={classes.cardForPaidCaregiverRadios}>
            <TrueLinkRadios
              ariaLabel="paid caregiver question"
              isFormik
              name="cardForPaidCaregiver"
              onChange={(event) => handleRadioSelect(event)}
              options={paidCaregiverAnswerOptions}
              required
              value={values.cardForPaidCaregiver}
            />
          </div>
        </>
      )}
      {parentGuardianQuestionIsRequired(values.cardFor) && $tlf.flipperShowChildSignUpFlow && (
        <>
          <CardText>
            Are you the parent or guardian of the teen or child?{' '}
            <TrueLinkTooltip
              tooltipContent={
                <Typography>
                  We collect this information so that we can set your card up correctly.
                </Typography>
              }
            >
              <TrueLinkIcon className={classes.activityIcon} icon="circle-question fa-regular" />
            </TrueLinkTooltip>
          </CardText>
          <ErrorMessage className={classes.errorMessage} component="p" name="cardForTeenOrChild" />
          <div className={classes.cardForPaidCaregiverRadios}>
            <TrueLinkRadios
              ariaLabel="parent guardian question"
              name="cardForTeenOrChild"
              onChange={(event) => handleRadioSelect(event, 'teen-or-child')}
              options={parentGuardianAnswerOptions}
              required
              value={values.cardForTeenOrChild}
            />
          </div>
        </>
      )}
    </>
  );
}

RolesForm.propTypes = {
  onChange: PropTypes.func.isRequired,
  paidCaregiverQuestionIsRequired: PropTypes.func,
  parentGuardianQuestionIsRequired: PropTypes.func,
  setFieldValue: PropTypes.func.isRequired,
  setFieldTouched: PropTypes.func.isRequired,
  values: PropTypes.shape({
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    cardFor: PropTypes.array.isRequired,
    cardForOther: PropTypes.string.isRequired,
    cardForPaidCaregiver: PropTypes.string,
    cardForTeenOrChild: PropTypes.string,
  }).isRequired,
};
